export const meta = {
  id: 'words',
  order: 7,
  titleFi: 'Lukusanat ja sananmuodostus',
  titleEn: 'Numerals and word building',
  topics: [
    { id: 'numerals', titleFi: 'Lukusanat', titleEn: 'Numerals' },
    { id: 'word-building', titleFi: 'Sananmuodostus', titleEn: 'Word building' },
    // the approved house style of docs/design/SANAPAJA.md (data/curated/sanapaja/style.json), one card per meaning:
    // grammar/paateopas-cards.ts, grammar/paateopas.tsx
    { id: 'paateopas', titleFi: 'Pääteopas', titleEn: 'Guide to endings' },
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_numerals',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Numerals_1',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Compound_numbers',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Derivational_Affixes',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Phrases_2',
    'https://dothraki.com/2013/08/valyrian-numerals/',
  ],
}

<Topic id="numerals">

<Fi>

<Define id="lukusana" /> Luvut jakautuvat neljään ryhmään:

- **1–9 ovat tavallisia adjektiiveja.** Ne <Term id="mukautuminen">mukautuvat</Term> substantiiviinsa sijassa, suvussa ja luvussa ja tulevat lähes aina sen edelle. *Yksi* on yleensä yksikössä ja 2–9 monikossa, mutta Petersonin mukaan lukusana on samassa luvussa kuin substantiivi: aidon <Term id="muutama">muutaman</Term> kanssa myös *yksi* on monikossa (<Hv glossed>mēriar lentun</Hv> ’yksi yhteisö’), <Term id="kollektiivi">kollektiivin</Term> kanssa yksikössä (<Hv glossed>mēre mentyr</Hv> ’yksi armeija’). Lukujen 2–9 kanssa substantiivi on monikossa siinä sijassa, jota lause vaatii: <Hv glossed>lantyz zaldrīzesse</Hv> ’kaksi lohikäärmettä’. Suomen <Term id="partitiivi">partitiivi</Term> (*kaksi lohikäärmettä*) johtaa tässä harhaan: kun suomi sanoo *rakastaa seitsemäätoista tyttöä*, valyriassa sekä luku että tytöt ovat monikon akkusatiivissa (ensimmäinen esimerkki alla).
- **Kymmenet eivät taivu.** <Hv glossed>ampa</Hv> ’10’ ja kymmenluvut (<Hv>-ēpsa</Hv>) pysyvät aina samoina. Väliin jäävissä luvuissa taipuva ykkönen tulee ensin ja kymmenluku perään <Term id="konjunktiivinen-pidennys">konjunktiivisella pidennyksellä</Term>, samalla ’ja’-keinolla kuin sanaluetteloissa (<TopicLink id="coordination" />): <Hv glossed>izula ampā</Hv> ’14’, <Hv glossed>mēre lantēpsā</Hv> ’21’. Kuten suomen *neljätoista*: vain alkuosa taipuu (*neljällätoista*). Luvussa 11 <Hv l="mēre">mēre</Hv> on substantiivin kanssa monikossa, <Hv glossed>mēri ampā</Hv>, koska yksitoista ei ole enää yksi.
- **Sadat** ovat <Term id="yhdyssana">yhdyssanoja</Term> sanasta <Hv glossed>gār</Hv> ’100’ (<Hv glossed>zūgār</Hv> ’400’ < <Hv>izula</Hv>), eivätkä nekään taivu. Loppuosan voi liittää kahdella tavalla. Petersonin omassa esimerkissä se tulee suoraan perään: <Hv glossed>gār mēri lantēpsā</Hv> ’121’, ja samoin Daenerysin luvussa 163 (alla). Duolingon ohjeiden esimerkissä 459 (alla) ja wikin lukusanasivun esimerkissä 410 väliin tulee <Hv glossed>se</Hv> ’ja’ (ei suomen *se*).
- **Tuhat**, <Hv glossed>pyrys</Hv>, on substantiivi: se on lauseen vaatimassa sijassa, ja laskettava on monikon genetiivissä, <Hv glossed>pyrys zaldrīzoti</Hv> ’tuhat lohikäärmettä’. Suomessa laskettava on tässäkin partitiivissa, valyriassa genetiivissä kuin ’lohikäärmeiden tuhat’. Tuhansia laskettaessa luku mukautuu sanaan <Hv>pyrys</Hv>: <Hv glossed>lantyz jēnqēpsā pyryssy zaldrīzoti</Hv> ’82 000 lohikäärmettä’.

</Fi>

<En>

<Define id="lukusana" /> The numerals fall into four groups:

- **1–9 are ordinary adjectives.** They <Term id="mukautuminen">agree</Term> with their noun in case, gender and number and almost always come before it. *One* is usually singular and 2–9 plural, but according to Peterson the numeral follows the noun’s own number: with a true <Term id="muutama">paucal</Term> even *one* is plural (<Hv glossed>mēriar lentun</Hv> ‘one community’), with a <Term id="kollektiivi">collective</Term> it is singular (<Hv glossed>mēre mentyr</Hv> ‘one army’). With 2–9 the noun is plural in whatever case the sentence needs: <Hv glossed>lantyz zaldrīzesse</Hv> ‘two dragons’. The Finnish <Term id="partitiivi">partitive</Term> (*kaksi lohikäärmettä*) is no guide here: where Finnish says *rakastaa seitsemäätoista tyttöä* ‘loves seventeen girls’, High Valyrian has both the numeral and the girls in the accusative plural (the first example below).
- **Tens do not inflect.** <Hv glossed>ampa</Hv> ‘10’ and the tens (<Hv>-ēpsa</Hv>) never change. In between, the inflecting unit comes first and the ten follows with <Term id="konjunktiivinen-pidennys">conjunctive lengthening</Term>, the same ‘and’ device as in lists of words (<TopicLink id="coordination" />): <Hv glossed>izula ampā</Hv> ‘14’, <Hv glossed>mēre lantēpsā</Hv> ‘21’. Just as in Finnish *neljätoista*, only the first part inflects (*neljällätoista*). In 11, <Hv l="mēre">mēre</Hv> is plural with a noun, <Hv glossed>mēri ampā</Hv>, because eleven is no longer one.
- **Hundreds** are <Term id="yhdyssana">compounds</Term> of <Hv glossed>gār</Hv> ‘100’ (<Hv glossed>zūgār</Hv> ‘400’ < <Hv>izula</Hv>) and do not inflect either. The rest follows in one of two ways. In Peterson’s own example it follows directly: <Hv glossed>gār mēri lantēpsā</Hv> ‘121’, and so it does in Daenerys’s 163 (below). In the Duolingo notes’ example 459 (below) and the wiki numerals page’s example 410, <Hv glossed>se</Hv> ‘and’ comes in between (not Finnish *se* ‘it’).
- **Thousand**, <Hv glossed>pyrys</Hv>, is a noun: it takes the case the sentence needs, and what is counted is in the genitive plural, <Hv glossed>pyrys zaldrīzoti</Hv> ‘a thousand dragons’. Finnish has the partitive here too; High Valyrian has a genitive, like ‘a thousand of dragons’. To count thousands, the numeral agrees with <Hv>pyrys</Hv>: <Hv glossed>lantyz jēnqēpsā pyryssy zaldrīzoti</Hv> ‘82,000 dragons’.

</En>

<FormGrid lemmas="lanta,hāre,izula,tōma" cols="pre.lunar.nom.pl,pre.solar.nom.pl,pre.terrestrial.nom.pl,pre.aquatic.nom.pl,pre.lunar.acc.pl" colsFi="kuusuku;aurinkosuku;maasuku;vesisuku;kuusuku, akkusatiivi" colsEn="lunar;solar;terrestrial;aquatic;lunar, accusative" fi="Luvut 2–5 mukautuvat: monikon nominatiivi suvuittain ja yksi akkusatiivi" en="Numerals 2–5 agree: nominative plural by gender, and one accusative" />

<Compare fi="lohikäärme|ttä" hv="zaldrīz|esse" label="yksikön partitiivi ~ monikon nominatiivi" />

<Example id="ex-6a699d4a0aec" />

<Interlinear hv="Zūgār se vōr|i tōmēpsā zaldrīz|esse" gloss="neljäsataa ja yhdeksän-NOM.PL viisikymmentä=ja lohikäärme-NOM.PL" fi="neljäsataaviisikymmentäyhdeksän lohikäärmettä" en="four hundred and fifty-nine dragons" />

<H fi="Luvut sanoina" en="The numerals as words" />

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th></th><th><T fi="1–9 (taipuu)" en="1–9 (inflects)" /></th><th><T fi="× 10 (ei taivu)" en="× 10 (fixed)" /></th><th><T fi="× 100 (ei taivu)" en="× 100 (fixed)" /></th><th><T fi="järjestysluku" en="ordinal" /></th></tr></thead>
  <tbody>
    <tr><td>1</td><td><Hv glossed>mēre</Hv></td><td><Hv glossed>ampa</Hv></td><td><Hv glossed>gār</Hv></td><td><Hv glossed>ēlie</Hv></td></tr>
    <tr><td>2</td><td><Hv glossed>lanta</Hv></td><td><Hv glossed>lantēpsa</Hv></td><td><Hv glossed>langār</Hv></td><td><Hv glossed>tȳne</Hv></td></tr>
    <tr><td>3</td><td><Hv glossed>hāre</Hv></td><td><Hv glossed>hārēpsa</Hv></td><td><Hv glossed>hārgār</Hv></td><td><Hv glossed>saelie</Hv></td></tr>
    <tr><td>4</td><td><Hv glossed>izula</Hv></td><td><Hv glossed>izulēpsa</Hv></td><td><Hv glossed>zūgār</Hv></td><td><Hv glossed>izunnie</Hv></td></tr>
    <tr><td>5</td><td><Hv glossed>tōma</Hv></td><td><Hv glossed>tōmēpsa</Hv></td><td><Hv glossed>tōngār</Hv></td><td><Hv glossed>tōmelie</Hv></td></tr>
    <tr><td>6</td><td><Hv glossed>bȳre</Hv></td><td><Hv glossed>bȳrēpsa</Hv></td><td><Hv glossed>bȳrgār</Hv></td><td><Hv glossed>byllie</Hv></td></tr>
    <tr><td>7</td><td><Hv glossed>sīkuda</Hv></td><td><Hv glossed>sīkudēpsa</Hv></td><td><Hv glossed>sīgār</Hv></td><td><Hv glossed>sīglie</Hv></td></tr>
    <tr><td>8</td><td><Hv glossed>jēnqa</Hv></td><td><Hv glossed>jēnqēpsa</Hv></td><td><Hv glossed>jēngār</Hv></td><td><Hv glossed>jēnqelie</Hv></td></tr>
    <tr><td>9</td><td><Hv glossed>vōre</Hv></td><td><Hv glossed>vōrēpsa</Hv></td><td><Hv glossed>vōrgār</Hv></td><td><Hv glossed>vollie</Hv></td></tr>
  </tbody>
</table>
</div>

<Fi>

<Term id="jarjestysluku">**Järjestysluvut**</Term> ovat <Term id="adjektiiviluokka">III-luokan</Term> adjektiiveja johtimella <Hv>-lie</Hv> (myös <Hv>-rie</Hv>, <Hv>-nie</Hv>), ja ensimmäiset ovat epäsäännöllisiä kuten suomessakin (*yksi → ensimmäinen*). Muita: 10. <Hv>amplie</Hv>, 11. <Hv>kūrie</Hv>, 12. <Hv>ñallie</Hv>, 13. <Hv glossed>saelie ampā</Hv>, 20. <Hv>lantīblie</Hv>, 100. <Hv>gallie</Hv>, 1000. <Hv>pyrȳzlie</Hv>. Ne taipuvat kuten muutkin adjektiivit: <Hv glossed>Tȳni Trēsi</Hv> ’Toiset pojat’.

**Ikä** kerrotaan perfektillä, ”olen nähnyt 36 vuotta” (esimerkki alla). Vuodet ovat silloin <Term id="kollektiivi">kollektiivissa</Term>: <Hv glossed>jēdari</Hv> on sanan <Hv glossed>jēda</Hv> ’vuosi’ kollektiivin akkusatiivi. Kollektiivia kohdellaan kuin yksikköä, joten myös lukusana on tässä yksikössä: <Hv l="bȳre">bȳre</Hv> eikä monikon <Hv l="bȳre">bȳri</Hv>. Ikää kysytään saman kollektiivin genetiivillä <Hv glossed>jēdaro</Hv> (<TopicLink id="questions" />).

</Fi>

<En>

<Term id="jarjestysluku">**Ordinals**</Term> are <Term id="adjektiiviluokka">class III</Term> adjectives in <Hv>-lie</Hv> (also <Hv>-rie</Hv>, <Hv>-nie</Hv>), and the first ones are irregular, as in Finnish and English (*one → first*). Others: 10th <Hv>amplie</Hv>, 11th <Hv>kūrie</Hv>, 12th <Hv>ñallie</Hv>, 13th <Hv glossed>saelie ampā</Hv>, 20th <Hv>lantīblie</Hv>, 100th <Hv>gallie</Hv>, 1000th <Hv>pyrȳzlie</Hv>. They inflect like any adjective: <Hv glossed>Tȳni Trēsi</Hv> ‘Second Sons’.

**Age** is told with the perfect, ‘I have seen 36 years’ (example below). The years are then in the <Term id="kollektiivi">collective</Term>: <Hv glossed>jēdari</Hv> is the collective accusative of <Hv glossed>jēda</Hv> ‘year’. A collective is treated like a singular, so the numeral is singular here too: <Hv l="bȳre">bȳre</Hv>, not the plural <Hv l="bȳre">bȳri</Hv>. Age is asked with the genitive of the same collective, <Hv glossed>jēdaro</Hv> (<TopicLink id="questions" />).

</En>

<Example id="ex-979b5e85bd99" />

<Note>

<Fi>

Daenerys sanoo luvun 163 ilman sanaa <Hv l="se">se</Hv>: <Hv>Gār hāri bȳrēpsā</Hv>, kuten Peterson sanoo luvun 121. Duolingon ohjeet opettavat satojen jälkeen sanan <Hv l="se">se</Hv>. Kumpikin tapa on virallinen, eikä wiki selitä eroa.

</Fi>

<En>

Daenerys says 163 without <Hv l="se">se</Hv>: <Hv>Gār hāri bȳrēpsā</Hv>, as Peterson says 121. The Duolingo notes teach <Hv l="se">se</Hv> after the hundreds. Both ways are official, and the wiki does not explain the difference.

</En>

<Line id="got-s4e04-020" />

</Note>

</Topic>

<Topic id="word-building">

<Fi>

Valyria rakentaa sanastonsa palasista kuten suomikin: <Term id="johdin">johtimista</Term> ja <Term id="etuliite">etuliitteistä</Term>. Kun tunnistat sanan lopun (<Hv>-tys</Hv>, <Hv>-lion</Hv>, <Hv>-ves</Hv>, <Hv>-qitta</Hv>) ja alun (<Hv>nā-</Hv>, <Hv>do-</Hv>), voit usein arvata merkityksen ennen kuin luet käännöksen. Linna, <Hv>sombāzmion</Hv>, on ”ison kilven paikka”, ja koulu, <Hv>bardulion</Hv>, ”kirjoittamisen paikka”.

**<Term id="yhdyssana">Yhdyssanoja</Term> on vähän.** Peterson sanoo suoraan, ettei valyria juuri muodosta yhdyssanoja: se käyttää johtimia ja etuliitteitä, ja moni johdin on alun perin kulunut kokonaisesta sanasta. Etuliitteellinen verbi (<Hv>jiōragon</Hv>, <Hv>mazōregon</Hv>) ei siis ole yhdyssana.

**Nimissä yhdyssanoja on.** Niissä <Term id="maarite">määrite</Term> tulee ensin, kuten suomen *lohikäärme|kivi*: lohikäärme <Hv glossed>Hūrlilio</Hv> (Moondancer) < <Hv glossed>hūra</Hv> ’kuu’ + <Hv glossed>lilagon</Hv> ’tanssia’ + <Hv>-io</Hv>, ja <Hv glossed>Biāllaodio</Hv> (Sheepstealer) < <Hv glossed>bianor</Hv> ’lammas’ + <Hv glossed>laodigon</Hv> ’varastaa’ + <Hv>-io</Hv>. Kaikki nimet eivät silti ole yhdyssanoja: esimerkiksi nimen loppu <Hv>-rys</Hv> ei ole oma sanansa, vaan <Hv>-ys</Hv> on tavallinen substantiivin pääte.

**Muut yhdyssanat.** Yhdistettyjä <Term id="sidesana">sidesanoja</Term> on muutama: <Hv l="sesīr-2">sesīr</Hv> < <Hv l="se">se</Hv> + <Hv>sīr</Hv>, samoin <Hv l="sepār-2">sepār</Hv> < <Hv l="se">se</Hv> + <Hv>pār</Hv>. Wikin sanasivut esittävät muutaman tavallisenkin sanan yhdyssanaksi, kuten <Hv glossed>zaldrīzes</Hv> ’lohikäärme’ < <Hv glossed>zālare</Hv> ’palava’ + <Hv glossed>rīza</Hv> ’lisko’, <Hv glossed>valzȳrys</Hv> ’aviomies’ < <Hv>vala</Hv> + <Hv>zȳrys</Hv> ja <Hv glossed>hunanne</Hv> ’aasi’ < <Hv glossed>hunes</Hv> ’jänis’ + <Hv glossed>anne</Hv> ’hevonen’, mutta näille ei ole Petersonin omaa lähdettä.

Verbejä johdetaan verbeistä omilla johtimillaan (<TopicLink id="suffixes" />) ja etuliitteillä (<TopicLink id="prefixes" />).

</Fi>

<En>

High Valyrian builds its vocabulary from parts, as Finnish does: <Term id="johdin">suffixes</Term> and <Term id="etuliite">prefixes</Term>. Once you recognise the end of a word (<Hv>-tys</Hv>, <Hv>-lion</Hv>, <Hv>-ves</Hv>, <Hv>-qitta</Hv>) and its start (<Hv>nā-</Hv>, <Hv>do-</Hv>), you can often guess the meaning before reading the translation. A castle, <Hv glossed>sombāzmion</Hv>, is a ‘place of the big shield’, and a school, <Hv glossed>bardulion</Hv>, a ‘place of writing’.

**<Term id="yhdyssana">Compounds</Term> are rare.** Peterson says plainly that High Valyrian “isn’t really a compounding language”: it uses suffixes and prefixes, and many suffixes started out as whole words that wore down. A prefixed verb (<Hv>jiōragon</Hv>, <Hv>mazōregon</Hv>) is therefore not a compound.

**Names do have compounds.** The <Term id="maarite">modifier</Term> comes first, as in Finnish *lohikäärme|kivi*: the dragon <Hv glossed>Hūrlilio</Hv> ‘Moondancer’ < <Hv glossed>hūra</Hv> ‘moon’ + <Hv glossed>lilagon</Hv> ‘dance’ + <Hv>-io</Hv>, and <Hv glossed>Biāllaodio</Hv> ‘Sheepstealer’ < <Hv glossed>bianor</Hv> ‘sheep’ + <Hv glossed>laodigon</Hv> ‘steal’ + <Hv>-io</Hv>. Not every name is one, though: a name ending in <Hv>-rys</Hv>, for example, has no word *rys* in it; <Hv>-ys</Hv> is an ordinary noun ending.

**Other compounds.** There are a few compound <Term id="sidesana">conjunctions</Term>: <Hv l="sesīr-2">sesīr</Hv> < <Hv l="se">se</Hv> + <Hv>sīr</Hv>, and likewise <Hv l="sepār-2">sepār</Hv> < <Hv l="se">se</Hv> + <Hv>pār</Hv>. The wiki’s word pages present a few everyday words as compounds, such as <Hv glossed>zaldrīzes</Hv> ‘dragon’ < <Hv glossed>zālare</Hv> ‘burning’ + <Hv glossed>rīza</Hv> ‘lizard’, <Hv glossed>valzȳrys</Hv> ‘husband’ < <Hv>vala</Hv> + <Hv>zȳrys</Hv> and <Hv glossed>hunanne</Hv> ‘donkey’ < <Hv glossed>hunes</Hv> ‘hare’ + <Hv glossed>anne</Hv> ‘horse’, but Peterson himself gives no source for these.

Verbs are derived from verbs with their own suffixes (<TopicLink id="suffixes" />) and with prefixes (<TopicLink id="prefixes" />).

</En>

<Fi>

Jokainen johdin merkityksineen, saumoineen ja Petersonin esimerkkeineen: <TopicLink id="paateopas" />.

</Fi>

<En>

Every suffix with its meaning, its seam and Peterson’s examples: <TopicLink id="paateopas" />.

</En>

<Fi>

**<Term id="johdos">Johdos</Term> taipuu kuten muutkin substantiivit.** Sanakirja antaa jokaiselle johdokselle suvun ja <Term id="deklinaatio">taivutusluokan</Term>, eikä se välttämättä taivu kuten kantasanansa. Esimerkiksi rakennussanaston <Hv>-ion</Hv>- ja <Hv>-lion</Hv>-sanat ovat kaikki maasukuisia ja taipuvat samalla tavalla.

</Fi>

<En>

**A <Term id="johdos">derived word</Term> declines like any other noun.** The dictionary gives each one a gender and a <Term id="deklinaatio">declension</Term>, which need not be those of the word it comes from. The building words in <Hv>-ion</Hv> and <Hv>-lion</Hv>, for instance, are all terrestrial and decline alike.

</En>

<FormGrid lemmas="brāedāzma,riñītsos,qīzalbar,zenturlion,gevives" cols="nom.sg,acc.sg,gen.sg,loc.sg,nom.pl" fi="Johdokset taipuvat sukunsa ja luokkansa mukaan" en="Derived words decline by their gender and class" />

<Example id="ex-2de825d91902" />

<H fi="Substantiivi adjektiivina" en="A noun used as an adjective" />

<Fi>

Joskus valmiiksi taivutettu substantiivi toimii adjektiivina. <Hv glossed>Ēngoso</Hv> on sanan <Hv glossed>ēngos</Hv> ’kieli’ instrumentaali, mutta substantiivin edellä se tarkoittaa ’puhelias’. Samoin <Hv glossed>averilloma</Hv> ’humalainen’ (sanan <Hv glossed>averilla</Hv> ’viini’ komitatiivi, ”viinin kanssa”), <Hv glossed>zaldrīzerme</Hv> ’voittamaton’ (sanan <Hv glossed>zaldrīzes</Hv> ’lohikäärme’ kollektiivin komitatiivi, ”kaikkien lohikäärmeiden kanssa”) ja <Hv glossed>udrimmi</Hv> ’nokkela’ (sanan <Hv glossed>udir</Hv> ’sana’ muutaman komitatiivi, ”muutaman sanan kanssa”). Ne ovat lähes aina <Term id="etuasema">etuasemassa</Term>, eivätkä ne <Term id="mukautuminen">**mukaudu**</Term> kuvaamaansa sanaan, koska ne ovat yhä substantiiveja. Suomessa samoin käyttäytyy genetiiviattribuutti: *Suomen lippu*, *Suomen lipulle*. Tällaiset sanat opetellaan sanoina, ei säännöllä.

</Fi>

<En>

Sometimes a noun already inflected for a case works as an adjective. <Hv glossed>Ēngoso</Hv> is the instrumental of <Hv glossed>ēngos</Hv> ‘tongue, language’, but before a noun it means ‘talkative’. Likewise <Hv glossed>averilloma</Hv> ‘drunk’ (the comitative of <Hv glossed>averilla</Hv> ‘wine’, ‘with wine’), <Hv glossed>zaldrīzerme</Hv> ‘invincible’ (the collective comitative of <Hv glossed>zaldrīzes</Hv> ‘dragon’, ‘with all dragons’) and <Hv glossed>udrimmi</Hv> ‘witty’ (the paucal comitative of <Hv glossed>udir</Hv> ‘word’, ‘with a few words’). They almost always stand <Term id="etuasema">before the noun</Term> and do <Term id="mukautuminen">**not agree**</Term> with it, because they are still nouns. The Finnish genitive attribute behaves the same way: *Suomen lippu*, *Suomen lipulle*. These are learnt as words, not by a rule.

</En>

<Compare hvFi="voittamattoman johtajan" hvEn="the invincible leader" fi="Suome|n lipun" hv="zaldrīzerme jent|i" label="taipumaton määrite: genetiiviattribuutti ~ adjektiivinen substantiivi (akkusatiivissa)" />

<Example id="ex-c1203ce0631a" />

<Fi>

Huomaa esimerkissä myös <Hv>dāri</Hv>: ’puhua kuninkaalle’ on valyriaksi akkusatiivi eikä suomen allatiivi (<TopicLink id="valence" />).

</Fi>

<En>

Note <Hv>dāri</Hv> in the example too: ‘speak to the king’ takes the accusative, not the Finnish allative (*kuninkaalle*) (<TopicLink id="valence" />).

</En>

</Topic>

<Topic id="paateopas">

<Fi>

Tämä opas kertoo, millä <Term id="johdin">johtimella</Term> valyriassa muodostetaan esimerkiksi henkilöä, paikkaa, välinettä tai tekoa tarkoittava sana ja miten johdin liitetään kantasanaan. Kun tunnet yhden sanan, näet korteista, miten siitä muodostetaan muita sanoja. Säännöt perustuvat Petersonin omiin <Term id="johdos">johdoksiin</Term>, ja jokaisen säännön alla näkyy, kuinka moni hänen sanoistaan noudattaa sitä. Kun Peterson käyttää samaan tarkoitukseen useampaa johdinta eikä hänen sanoistaan löydy selvää sääntöä, olemme valinneet niistä yhden, ja sen kohdalla lukee *meidän valintamme*. Samoilla säännöillä teemme omat sanamme.

Peterson on johtanut verbistä <Hv>sindigon</Hv> neljä sanaa neljän eri kortin säännöllä:

</Fi>

<En>

This guide tells which <Term id="johdin">suffix</Term> makes a person, a place, a tool or an act in High Valyrian, and how the suffix joins the word. Once you know one word, the cards show how its other words are made. The rules are gathered from Peterson’s own <Term id="johdos">derived words</Term>, and under each rule you see how many of his words follow it. Where Peterson uses more than one suffix for the same purpose and his words give no clear rule, we have chosen one of them, and it is marked *our choice*. We make our own words by the same rules.

Peterson has derived four words from the verb <Hv>sindigon</Hv>, each by the rule of a different card:

</En>

- <Seam s="sindi" e="lion" /> · <CardLink id="paikka" />
- <Seam s="sindi" e="kio" /> · <CardLink id="henkilo" />
- <Seam s="sind" e="iapos" /> · <CardLink id="pieni" />
- <Seam s="nā" e="sindigon" pre /> · <CardLink id="vastakohta" />

<EndingIndex />

<EndingCard id="henkilo" like="-ja">

<Fi>

Henkilö, jolla on jokin ammatti tai pysyvä tehtävä, tai henkilö, joka tunnetaan yhdestä teostaan.

</Fi>

<En>

A person with a trade or a lasting task, or a person known for one act.

</En>

<Use row="person.role.verb" fi="Rooli tai ammatti verbistä" en="A role or trade, from a verb">

<Fi>

<Term id="konsonanttivartalo">Konsonanttivartaloon</Term> tulee *tys*: vartalon lopun *b*, *d* ja *g* muuttuvat *p*:ksi, *t*:ksi ja *k*:ksi, *m* muuttuu *n*:ksi ja pitkä vokaali lyhenee. Vokaalivartalossa johdin on *rys* loppuvokaalin *e*, *i* tai *u* jälkeen ja *tys* *a*:n jälkeen. Rooleihin Peterson käyttää yhtä usein myös johdinta <Hv>-io</Hv>; meidän valintamme on <Hv>-tys</Hv>, jotta ammattien johdin olisi aina sama.

</Fi>

<En>

A <Term id="konsonanttivartalo">consonant stem</Term> takes *tys*: a final *b*, *d* or *g* becomes *p*, *t* or *k*, *m* becomes *n*, and a long vowel shortens. On a vowel stem the suffix is *rys* after *e*, *i* or *u* and *tys* after *a*. Peterson uses <Hv>-io</Hv> for roles just as often; our choice is <Hv>-tys</Hv>, so that trades always have the same suffix.

</En>

<Ex><Hv>tepagon</Hv><Seam s="tep" e="tys" /></Ex>

<Ex><Hv>arghugon</Hv><Seam s="arghu" e="rys" /></Ex>

<Ex><Hv>jemagon</Hv><Seam s="jen" e="tys" /></Ex>

<Ex ours><Hv>zgiēñemagon</Hv><Seam s="zgiēñen" e="tys" /></Ex>

<Alt row="person.role.verb" sense="-io.agent" fi="kun saman verbin sanaperheessä on jo samanlainen -io-sana tai kun -tys ei sovi" en="when the verb’s family already has an -io word of this kind, or when -tys does not work">

<Ex><Hv>keragon</Hv><Seam s="ker" e="io" /></Ex>

<Ex><Hv>lioragon</Hv><Seam s="lior" e="io" l="liorio" /></Ex>

<Ex><Hv>sētegon</Hv><Seam s="sēte" e="kio" /></Ex>

</Alt>

</Use>

<Use row="person.act.verb" fi="Yhdestä teosta nimetty henkilö" en="A person named for one act">

<Fi>

Konsonanttivartaloon tulee *io*, <Term id="vokaalivartalo">vokaalivartaloon</Term> *kio*. Muutamassa vokaalivartalossa loppuvokaali kuitenkin katoaa ja johdin on pelkkä *io*, kuten sanassa <Hv>ūndio</Hv>. Monella näistä sanoista on kielteinen sävy: varas, petturi, myrkyttäjä.

</Fi>

<En>

A consonant stem takes *io*, a <Term id="vokaalivartalo">vowel stem</Term> *kio*. In a few vowel stems, though, the final vowel drops and the suffix is plain *io*, as in <Hv>ūndio</Hv>. Many of these words have a negative ring: thief, traitor, poisoner.

</En>

<Ex><Hv>laodigon</Hv><Seam s="laodi" e="kio" /></Ex>

<Ex><Hv>ūndegon</Hv><Seam s="ūnd" e="io" /></Ex>

<Ex><Hv>kaerīnagon</Hv><Seam s="kaerīn" e="io" /></Ex>

<Alt row="person.act.verb" sense="-tys.person" fi="kun henkilö on pysyvä vastustaja tai valloittaja eikä yhden teon tekijä" en="for a lasting opponent or a conqueror rather than one act">

<Ex><Hv>ērinagon</Hv><Seam s="ērin" e="tys" l="ērintys" /></Ex>

<Ex><Hv>indigon</Hv><Seam s="indi" e="rys" /></Ex>

</Alt>

</Use>

<Use row="person.noun" fi="Henkilö, jonka työ liittyy substantiivin tarkoittamaan asiaan" en="A person whose work is with a thing, from a noun">

<Fi>

Substantiivin vartaloon tulee *tys* ja äänteet muuttuvat samoin kuin verbeissä: *b*, *d* ja *g* muuttuvat *p*:ksi, *t*:ksi ja *k*:ksi, ja pitkä vokaali lyhenee.

</Fi>

<En>

The noun’s stem takes *tys*, with the same sound changes as for verbs: *b*, *d* and *g* become *p*, *t* and *k*, and a long vowel shortens.

</En>

<Ex><Hv>lōgor</Hv><Seam s="lok" e="tys" /></Ex>

<Ex><Hv l="obo">obo</Hv><Seam s="op" e="tys" /></Ex>

<Ex><Hv>mēny</Hv><Seam s="men" e="tys" /></Ex>

<Alt row="person.noun" sense="-io.augmentative" fi="aikuinen tai arvovaltainen henkilö" en="a grown or influential person">

<Ex><Hv>rhotton</Hv><Seam s="rhott" e="io" /></Ex>

<Ex><Hv>jovēnnon</Hv><Seam s="jovēnn" e="io" /></Ex>

</Alt>

</Use>

<Fi>

Älä sekoita: <Hv>-mio</Hv> ei tee henkilöiden nimiä. Petersonin <Hv>-mio</Hv>-sanat ovat esineiden ja luonnonvoimien nimiä (<Hv>hilmio</Hv>, <Hv>jelmio</Hv>).

</Fi>

<En>

Do not confuse: <Hv>-mio</Hv> does not make people. Peterson’s <Hv>-mio</Hv> words name things and natural forces (<Hv>hilmio</Hv>, <Hv>jelmio</Hv>).

</En>

</EndingCard>

<EndingCard id="adjektiivista">

<Fi>

Adjektiivista tehty substantiivi tarkoittaa henkilöä tai asiaa, jolla on adjektiivin ominaisuus. Johdin riippuu <Term id="adjektiiviluokka">adjektiiviluokasta</Term>. <Term id="partisiippi">Partisiippikin</Term> on adjektiivi: preesensin ja aoristin aktiivin partisiipit ovat <Term id="adjektiiviluokka">II-luokan</Term> adjektiiveja ja passiivin partisiipit <Term id="adjektiiviluokka">I-luokan</Term>, joten samat säännöt tekevät partisiipista tekijää tai kohdetta tarkoittavan sanan.

</Fi>

<En>

A noun made from an adjective means a person or a thing that has the adjective’s quality. The ending depends on the <Term id="adjektiiviluokka">adjective class</Term>. A <Term id="partisiippi">participle</Term> is an adjective too: the present and aorist active participles are <Term id="adjektiiviluokka">class II</Term> and the passive participles <Term id="adjektiiviluokka">class I</Term>, so the same rule makes a doer or a target out of a participle.

</En>

<Use row="person.adj-1" fi="I-luokan adjektiivista" en="From a class I adjective">

<Fi>

Vartaloon tulee *y*.

</Fi>

<En>

The stem takes *y*.

</En>

<Ex><Hv>nākōrila</Hv><Seam s="nākōril" e="y" /></Ex>

<Ex><Hv>tolmīha</Hv><Seam s="tolmīh" e="y" /></Ex>

<Alt row="person.adj-1" sense="-tys.person" fi="rooli tai asema, joka on saanut nimensä ominaisuudesta" en="a role or standing named after the quality">

<Ex><Hv>vōka</Hv><Seam s="vok" e="tys" /></Ex>

<Ex><Hv>nūda</Hv><Seam s="nut" e="tys" /></Ex>

</Alt>

</Use>

<Use row="person.adj-2" fi="II-luokan adjektiivista" en="From a class II adjective">

<Fi>

Loppu-*e* katoaa ja tilalle tulee *os*.

</Fi>

<En>

The final *e* drops and *os* takes its place.

</En>

<Ex><Hv>drēje</Hv><Seam s="drēj" e="os" l="drējos" /></Ex>

<Ex><Hv l="idañe">idañe</Hv><Seam s="idañ" e="os" l="idaños" /></Ex>

<Ex><Hv>nāmorghūlilare</Hv><Seam s="nāmorghūlilar" e="os" fi="kuolematon (olento)" en="immortal (being)" /></Ex>

</Use>

<Use row="person.adj-3" fi="III-luokan adjektiivista" en="From a class III adjective">

<Fi>

Loppu-*ie* muuttuu *ys*:ksi.

</Fi>

<En>

The final *ie* becomes *ys*.

</En>

<Ex><Hv>zōbrie</Hv><Seam s="zōbr" e="ys" /></Ex>

</Use>

<Use row="person.habitual.verb" fi="Aktiivin partisiipista: se, joka tekee jotakin tavakseen" en="From an active participle: one who does something as a habit">

<Fi>

Aoristin partisiipista tulee se, joka tekee jotakin tavakseen, preesensin partisiipista se, joka tekee sitä juuri nyt. Loppu-*e* katoaa ja tilalle tulee *os*.

</Fi>

<En>

The aorist participle gives one who does something as a habit, the present participle one who is doing it right now. The final *e* drops and *os* takes its place.

</En>

<Ex><Hv>botire</Hv><Seam s="botir" e="os" /></Ex>

<Ex><Hv>jollōrire</Hv><Seam s="jollōrir" e="os" /></Ex>

<Ex><Hv>raqire</Hv><Seam s="raqir" e="os" /></Ex>

<Ex><Hv>gūrēñare</Hv><Seam s="gūrēñar" e="os" /></Ex>

</Use>

<Use row="person.patient.verb" fi="Passiivin partisiipista: se, johon teko kohdistuu" en="From a passive participle: one the act is done to">

<Fi>

Loppu-*a* katoaa ja tilalle tulee *y*. Sana tarkoittaa henkilöä tai laskettavaa asiaa, johon teko kohdistuu.

</Fi>

<En>

The final *a* drops and *y* takes its place: a person or a countable thing that something has been done to.

</En>

<Ex><Hv>ozgūrota</Hv><Seam s="ozgūrot" e="y" /></Ex>

<Ex><Hv>inditeta</Hv><Seam s="inditet" e="y" /></Ex>

<Ex><Hv>irūda</Hv><Seam s="irūd" e="y" /></Ex>

</Use>

</EndingCard>

<EndingCard id="teko" like="-minen, -us, -o">

<Fi>

Teon tai toiminnan nimi verbistä: rakkaus, voitto, uni, häät.

</Fi>

<En>

The name of an act or an activity, from a verb: love, victory, sleep, a wedding.

</En>

<Use row="action.verb" fi="Teko tai toiminta verbistä" en="An act or activity, from a verb">

<Fi>

Vartaloon tulee *non*. Jos vartalo päättyy *n*:ään, *ñ*:ään, *l*:ään tai *nd*:hen, loppuäänne sulautuu johtimen *n*:n kanssa *nn*:ksi; *lj* muuttuu *l*:ksi ja *s* *z*:ksi.

</Fi>

<En>

The stem takes *non*. A stem ending in *n*, *ñ*, *l* or *nd* merges with the *n* into *nn*; *lj* becomes *l* and *s* becomes *z*.

</En>

<Ex><Hv>raqagon</Hv><Seam s="raq" e="non" /></Ex>

<Ex><Hv>ērinagon</Hv><Seam s="ērin" e="non" /></Ex>

<Ex><Hv>ēdrugon</Hv><Seam s="ēdru" e="non" /></Ex>

<Alt row="action.verb" sense="-on.action-verb" fi="kun vartalo päättyy konsonanttiyhtymään, jonka perään n ei sovi" en="when the stem ends in a consonant cluster that n cannot follow">

<Ex><Hv>verdagon</Hv><Seam s="verd" e="on" l="verdon" /></Ex>

<Ex><Hv>umbagon</Hv><Seam s="umb" e="on" l="umbon" /></Ex>

</Alt>

<Alt row="action.verb" sense="-ion.activity-verb" fi="pysyvä tila tai se, mitä toiminnasta syntyy" en="a lasting state, or what comes of the activity">

<Ex><Hv>kostagon</Hv><Seam s="kost" e="ion" /></Ex>

<Ex><Hv>lilagon</Hv><Seam s="lil" e="ion" /></Ex>

</Alt>

<Alt row="action.verb" sense="-ion.abstract-adj" fi="abstrakti sana aktiivin partisiipista" en="an abstract noun, from an active participle">

<Ex><Hv>qilōnare</Hv><Seam s="qilōnar" e="ion" /></Ex>

<Ex><Hv>dohaerire</Hv><Seam s="dohaerir" e="ion" /></Ex>

</Alt>

</Use>

<Use row="event.verb" fi="Tapahtuma tai tilaisuus verbistä" en="An event or occasion, from a verb">

<Fi>

Sama *non* kuin teon nimessä.

</Fi>

<En>

The same *non* as for an act.

</En>

<Ex><Hv fi="vihkiä" en="to wed">dīnagon</Hv><Seam s="dīn" e="non" /></Ex>

<Ex><Hv>māzigon</Hv><Seam s="māzi" e="non" /></Ex>

<Ex><Hv>gierūljagon</Hv><Seam s="gierūl" e="non" /></Ex>

<Alt row="event.verb" sense="-ion.activity-verb" fi="tilaisuus, joka on nimetty sen mukaan, mitä siinä tehdään" en="an occasion named for what is done there">

<Ex><Hv>soljagon</Hv><Seam s="sol" e="ion" /></Ex>

</Alt>

</Use>

<Use row="event.contest.verb" fi="Kilpailu, huutokauppa tai oikeudenkäynti" en="A contest, an auction or a trial">

<Fi>

Vartaloon tulee *nes*.

</Fi>

<En>

The stem takes *nes*.

</En>

<Ex><Hv>dakogon</Hv><Seam s="dako" e="nes" /></Ex>

<Ex><Hv>rhovagon</Hv><Seam s="rhov" e="nes" /></Ex>

<Alt row="event.contest.verb" sense="-ion.activity-verb" fi="turnaus tai taitojen näyttäminen" en="a tournament, or a show of skill">

<Ex><Hv>kōttegon</Hv><Seam s="kōtt" e="ion" l="kōttion" /></Ex>

</Alt>

</Use>

</EndingCard>

<EndingCard id="tulos" like="-os, -e">

<Fi>

Se, mitä teosta tai asiasta syntyy tai jää: aine, tuote, jälki tai tulos.

</Fi>

<En>

What an act or a thing produces or leaves behind: a substance, a product, a trace or an outcome.

</En>

<Use row="result.verb" fi="Aine tai tuote verbistä" en="A substance or a product, from a verb">

<Fi>

Vartaloon tulee *illa*. Jos vartalo päättyy *i*:hin, se sulautuu johtimeen (<Hv>rhakigon</Hv> → <Hv>rhakilla</Hv>) ja *e*- tai *o*-loppuiseen vartaloon tulee pelkkä *lla*. Sana näyttää kuusukuiselta, mutta se on vesisukua.

</Fi>

<En>

The stem takes *illa*. An *i* stem merges its *i* into the suffix (<Hv>rhakigon</Hv> → <Hv>rhakilla</Hv>), and an *e* or *o* stem takes only *lla*. The word looks lunar but is aquatic.

</En>

<Ex><Hv>keragon</Hv><Seam s="ker" e="illa" /></Ex>

<Ex><Hv>qepēbagon</Hv><Seam s="qepēb" e="illa" /></Ex>

<Ex><Hv>vēttegon</Hv><Seam s="vētte" e="lla" /></Ex>

<Alt row="result.verb" sense="-y.patient-verb" fi="laskettava asia, johon teko kohdistuu tai joka siitä syntyy" en="a countable thing the act has or makes">

<Ex><Hv>jāelagon</Hv><Seam s="jāel" e="y" /></Ex>

<Ex><Hv>paghagon</Hv><Seam s="pagh" e="y" /></Ex>

</Alt>

<Alt row="result.verb" sense="-ys.result-participle" fi="abstrakti tulos tai jälki: preesensin aktiivin partisiipista" en="an abstract result or a trace: from the present active participle">

<Ex><Hv>ēdrure</Hv><Seam s="ēdrur" e="ys" /></Ex>

<Ex><Hv>jaelare</Hv><Seam s="jaelar" e="ys" /></Ex>

<Ex><Hv>zālare</Hv><Seam s="zālar" e="ys" /></Ex>

</Alt>

<Alt row="result.verb" sense="-ir.abstract-adj" fi="se, mitä tehdään tai on tehty, ei laskettavana esineenä: passiivin partisiipista" en="that which is done, not as a countable object: from a passive participle">

<Ex><Hv>gūrēnilaksa</Hv><Seam s="gūrēnilaks" e="ir" /></Ex>

<Ex><Hv>vētta</Hv><Seam s="vētt" e="ir" /></Ex>

<Ex><Hv>vestriarza</Hv><Seam s="vestriarz" e="ir" /></Ex>

</Alt>

</Use>

<Use row="result.noun" fi="Aine tai tuote substantiivista" en="A substance or a product, from a noun">

<Fi>

Vartaloon tulee *illa*. Jos sanalla on toinen vartalo, johdin tulee siihen.

</Fi>

<En>

The stem takes *illa*. A word with a second stem takes the suffix on that one.

</En>

<Ex><Hv>avero</Hv><Seam s="aver" e="illa" /></Ex>

<Ex><Hv>bianor</Hv><Seam s="biādr" e="illa" /></Ex>

<Ex><Hv l="vēzos">vēzos</Hv><Seam s="vēz" e="illa" /></Ex>

</Use>

</EndingCard>

<EndingCard id="valine" like="-in">

<Fi>

Esine, laite tai keino, jolla jotakin tehdään: istuin, tikkaat, kuppi.

</Fi>

<En>

An object, a device or a means to do something with: a seat, a ladder, a cup.

</En>

<Use row="tool.verb" fi="Väline verbistä" en="A tool, from a verb">

<Fi>

Konsonanttivartaloon tulee *vos*; vokaalivartaloon samoin ja loppuvokaali säilyy. Johdin *vos* ei sovi *ng*:n perään.

</Fi>

<En>

A consonant stem takes *vos*; so does a vowel stem, keeping its final vowel. The suffix *vos* cannot follow *ng*.

</En>

<Ex><Hv>dēmagon</Hv><Seam s="dēma" e="vos" /></Ex>

<Ex><Hv>hepagon</Hv><Seam s="hep" e="vos" /></Ex>

<Ex><Hv>hakogon</Hv><Seam s="hako" e="vos" /></Ex>

<Ex ours><Hv>ȳdragon</Hv><Seam s="ȳdra" e="vos" /></Ex>

<Alt row="tool.verb" sense="-y.tool" fi="ansa, pallo tai yksinkertainen työkalu" en="a trap, a ball or a plain tool">

<Ex><Hv>rāelagon</Hv><Seam s="rāel" e="y" /></Ex>

<Ex><Hv>sētegon</Hv><Seam s="sēt" e="y" /></Ex>

</Alt>

</Use>

<Use row="tool.hand.verb" fi="Käsityökalu aineen työstämiseen, konsonanttivartaloisesta verbistä" en="A hand tool for working a material, from a consonant-stem verb">

<Fi>

Vartaloon tulee *y*. Sana on kuusukua (aurinkosukuinen on vain <Hv>qilōny</Hv>).

</Fi>

<En>

The stem takes *y*. The word is lunar (only <Hv>qilōny</Hv> is solar).

</En>

<Ex><Hv>jukagon</Hv><Seam s="juk" e="y" /></Ex>

<Ex><Hv>grozagon</Hv><Seam s="groz" e="y" /></Ex>

<Ex><Hv>rāenagon</Hv><Seam s="rāen" e="y" /></Ex>

<Alt row="tool.hand.verb" sense="-vos.tool" fi="pistävä väline, kuten tikari tai hiilihanko" en="a pointed implement, such as a dagger or a poker">

<Ex><Hv>ohīlagon</Hv><Seam s="ohīl" e="vos" /></Ex>

<Ex><Hv>tēmagon</Hv><Seam s="tēm" e="vos" /></Ex>

</Alt>

<Alt row="tool.hand.verb" sense="-ītsos.small" fi="pienempi työkalu, kun samasta verbistä on jo isompi" en="a smaller tool beside the verb’s bigger one">

<Ex><Hv>jukagon</Hv><Seam s="juk" e="ītsos" /></Ex>

<Ex><Hv>rhupagon</Hv><Seam s="rhup" e="ītsos" /></Ex>

</Alt>

</Use>

<Use row="tool.noun" fi="Väline substantiivista" en="A tool, from a noun">

<Fi>

Vartaloon tulee *y*. Petersonilla on tästä vain kaksi sanaa; esineen nimen hän johtaa substantiivista useammin johtimella <Hv>-iapos</Hv>, joten harkitse kumpaakin.

</Fi>

<En>

The stem takes *y*. Peterson has only two such words; he makes objects from nouns more often with <Hv>-iapos</Hv>, so look at both.

</En>

<Ex><Hv>egros</Hv><Seam s="egr" e="y" /></Ex>

<Ex><Hv>ondos</Hv><Seam s="ond" e="y" /></Ex>

<Alt row="tool.noun" sense="-iapos.small" fi="esine, jonka nimi on johdettu X:stä mutta merkitys on siirtynyt" en="an object derived from X, its meaning shifted">

<Ex><Hv>ūly</Hv><Seam s="ūl" e="iapos" /></Ex>

<Ex><Hv>grevion</Hv><Seam s="grev" e="iapos" /></Ex>

<Ex><Hv>laodi</Hv><Seam s="laod" e="iapos" /></Ex>

</Alt>

</Use>

</EndingCard>

<EndingCard id="paikka" like="-la, -mo, -sto">

<Fi>

Paikka, jossa jotakin tehdään, säilytetään tai on.

</Fi>

<En>

A place where something is done, kept or found.

</En>

<Use row="place.verb-vowel" fi="Vokaalivartaloisesta verbistä" en="From a vowel-stem verb">

<Fi>

Vartaloon tulee *lion* ja vartalon loppuvokaali säilyy.

</Fi>

<En>

The stem takes *lion* and keeps its final vowel.

</En>

<Ex><Hv>bardugon</Hv><Seam s="bardu" e="lion" /></Ex>

<Ex><Hv>sindigon</Hv><Seam s="sindi" e="lion" /></Ex>

<Ex><Hv>dēdegon</Hv><Seam s="dēde" e="lion" /></Ex>

<Alt row="place.verb-vowel" sense="-ion.place-verb" fi="vain hyvästä syystä" en="only with a good reason">

<Ex><Hv>iōdegon</Hv><Seam s="iōd" e="ion" l="iōdion" /></Ex>

</Alt>

</Use>

<Use row="place.verb-consonant" fi="Konsonanttivartaloisesta verbistä" en="From a consonant-stem verb">

<Fi>

Vartaloon tulee *ion*; *ñ* muuttuu *i*:n edellä *n*:ksi (<Hv>viliñagon</Hv> → <Hv>vilinion</Hv>).

</Fi>

<En>

The stem takes *ion*; *ñ* becomes *n* before the *i* (<Hv>viliñagon</Hv> → <Hv>vilinion</Hv>).

</En>

<Ex><Hv>lioragon</Hv><Seam s="lior" e="ion" /></Ex>

<Ex><Hv>kisībagon</Hv><Seam s="kisīb" e="ion" /></Ex>

<Ex><Hv>botagon</Hv><Seam s="bot" e="ion" /></Ex>

<Alt row="place.verb-consonant" sense="-lion.place" fi="vain hyvästä syystä" en="only with a good reason">

<Ex><Hv>rijībagon</Hv><Seam s="rijīb" e="lion" /></Ex>

<Ex ours><Hv>keragon</Hv><Seam s="kera" e="lion" /></Ex>

<Fi>

Omassa sanassamme syy on se, että *ion*-johdoksen taivutusmuodot olisivat samoja kuin sanan <Hv>kerio</Hv> muodot. Lisäksi vartalon ja johtimen väliin on tullut *a*, toisin kuin Petersonin sanassa <Hv>rijīblion</Hv>.

</Fi>

<En>

In our word the reason is that the *ion* form’s inflected forms would coincide with those of <Hv>kerio</Hv>. It also has an *a* between the stem and the suffix, unlike Peterson’s <Hv>rijīblion</Hv>.

</En>

</Alt>

<Alt row="place.verb-consonant" sense="-urlion.place" fi="laaja alue, jota viljellään tai jolla tehdään työtä" en="a large worked area">

<Ex><Hv>grozagon</Hv><Seam s="groz" e="urlion" /></Ex>

</Alt>

</Use>

<Use row="place.noun" fi="Substantiivista" en="From a noun">

<Fi>

Vartaloon tulee *urlion*. Jos sanalla on toinen vartalo, johdin tulee siihen (<Hv>laes</Hv> → <Hv>laehurlion</Hv>).

</Fi>

<En>

The stem takes *urlion*. A word with a second stem takes the suffix on that one (<Hv>laes</Hv> → <Hv>laehurlion</Hv>).

</En>

<Ex><Hv>ārilla</Hv><Seam s="ārill" e="urlion" /></Ex>

<Ex><Hv>zentys</Hv><Seam s="zent" e="urlion" /></Ex>

<Ex><Hv>zgiēñemvos</Hv><Seam s="zgiēñemv" e="urlion" /></Ex>

<Alt row="place.noun" sense="-ion.place" fi="paikka, joka on nimetty sen mukaan, mitä siellä tapahtuu tai on" en="a place named for what goes on or is kept there">

<Ex><Hv>iderennes</Hv><Seam s="iderenn" e="ion" /></Ex>

<Ex><Hv>nūmalbar</Hv><Seam s="nūmalbr" e="ion" /></Ex>

<Ex><Hv>somby</Hv><Seam s="somb" e="ion" /></Ex>

</Alt>

<Alt row="place.noun" sense="-lion.place" fi="lattia tai työskentelyalue" en="a floor or an area of work">

<Ex><Hv>tegon</Hv><Seam s="teg" e="lion" /></Ex>

<Ex><Hv l="tembyr">tembyr</Hv><Seam s="tembyr" e="lion" /></Ex>

</Alt>

</Use>

<Use row="place.adjective" e="-ālion" fi="Adjektiivista" en="From an adjective">

<Fi>

Vartaloon tulee *ālion*.

</Fi>

<En>

The stem takes *ālion*.

</En>

<Ex><Hv>tista</Hv><Seam s="tist" e="ālion" /></Ex>

<Ex><Hv>trūma</Hv><Seam s="trūm" e="ālion" /></Ex>

<Ex><Hv>ȳgha</Hv><Seam s="ȳgh" e="ālion" /></Ex>

<Alt row="place.adjective" sense="-ion.place" fi="maastonmuoto, joka on nimetty ominaisuutensa mukaan" en="a landform named for its quality">

<Ex><Hv l="eglie">eglie</Hv><Seam s="egl" e="ion" /></Ex>

<Ex><Hv>ninka</Hv><Seam s="nink" e="ion" /></Ex>

</Alt>

<Alt row="place.adjective" sense="-urlion.place" fi="suunniteltu ja rakennettu paikka" en="a laid-out place">

<Ex><Hv>gevie</Hv><Seam s="gev" e="urlion" /></Ex>

</Alt>

</Use>

</EndingCard>

<EndingCard id="ominaisuus" like="-us, -uus">

<Fi>

Ominaisuuden nimi: kauneus, vapaus, terveys.

</Fi>

<En>

The quality itself: beauty, freedom, health.

</En>

<Use row="quality.adjective" fi="Adjektiivista" en="From an adjective">

<Fi>

Johdin riippuu adjektiiviluokasta. <Term id="adjektiiviluokka">I-luokan</Term> vartaloon tulee *āves*. <Term id="adjektiiviluokka">II-luokan</Term> sanasta katoaa loppu-*e* ja tilalle tulee *ves*; *z*-loppuisissa johdin tulee *r*-vartaloon. <Term id="adjektiiviluokka">III-luokan</Term> loppu-*ie* muuttuu *ives*:ksi.

</Fi>

<En>

The ending depends on the adjective class. A <Term id="adjektiiviluokka">class I</Term> stem takes *āves*. A <Term id="adjektiiviluokka">class II</Term> word drops its final *e* and takes *ves*; one ending in *z* takes it on its *r* stem. The <Term id="adjektiiviluokka">class III</Term> ending *ie* becomes *ives*.

</En>

<Ex><Hv>gevie</Hv><Seam s="gev" e="ives" /></Ex>

<Ex><Hv>dāez</Hv><Seam s="dāer" e="ves" /></Ex>

<Ex><Hv>rytsa</Hv><Seam s="ryts" e="āves" /></Ex>

<Alt row="quality.adjective" sense="-ion.abstract-adj" fi="II- ja III-luokassa: asia itse eikä sen aste" en="classes II and III: the thing itself rather than its degree">

<Ex><Hv>drēje</Hv><Seam s="drēj" e="ion" /></Ex>

<Ex><Hv>kempēgrie</Hv><Seam s="kempēgr" e="ion" /></Ex>

</Alt>

<Alt row="quality.adjective" sense="-ir.abstract-adj" fi="I-luokassa: se, mikä on X" en="class I: that which is X">

<Ex><Hv>pirta</Hv><Seam s="pirt" e="ir" /></Ex>

<Ex><Hv>nāqopsa</Hv><Seam s="nāqops" e="ir" /></Ex>

</Alt>

</Use>

<Use row="abstract.noun" fi="Abstrakti sana substantiivista" en="An abstract noun, from a noun">

<Fi>

Vartaloon tulee *ion*. Petersonin sanojen merkitykset ovat kaukana kantasanan merkityksestä, joten niitä ei voi arvata.

</Fi>

<En>

The stem takes *ion*. Peterson’s words drift far from their base, so the meaning cannot be guessed.

</En>

<Ex><Hv>dubys</Hv><Seam s="dub" e="ion" /></Ex>

<Ex><Hv>sȳndrilla</Hv><Seam s="sȳndrill" e="ion" /></Ex>

</Use>

</EndingCard>

<EndingCard id="pieni" like="-nen, -kainen">

<Fi>

Pieni X tai uusi asia, joka on saanut nimensä X:stä. Peterson on todennut, etteivät nämä kolme pienennysjohdinta ole samanmerkityksisiä – paitsi silloin, kun ovat. Ne valitaan sana kerrallaan.

</Fi>

<En>

A small X, or a new thing named after X. Peterson has said that the three diminutives are not synonymous, except when they are. They are chosen word by word.

</En>

<Use row="small.noun" fi="Pieni X, joka on yhä X" en="A small X that is still an X">

<Fi>

Vartaloon tulee *ītsos*; *ñ* säilyy.

</Fi>

<En>

The stem takes *ītsos*; *ñ* stays.

</En>

<Ex><Hv>kēli</Hv><Seam s="kēl" e="ītsos" /></Ex>

<Ex><Hv>riña</Hv><Seam s="riñ" e="ītsos" /></Ex>

<Ex><Hv>perzys</Hv><Seam s="perz" e="ītsos" /></Ex>

<Alt row="small.noun" sense="-iapos.small" fi="nuori kasvi" en="a young plant">

<Ex><Hv>guēse</Hv><Seam s="guēs" e="iapos" /></Ex>

</Alt>

</Use>

<Use row="small.new.noun" fi="Uusi asia, joka on saanut nimensä X:stä" en="A new thing named after X">

<Fi>

Vartaloon tulee *iapos*. Jos vartalo päättyy *i*:hin, se sulautuu johtimeen (<Hv>sindigon</Hv> → <Hv>sindiapos</Hv>) ja *ñ*:n jälkeen johtimen *i* katoaa (<Hv>ōños</Hv> → <Hv>ōñapos</Hv>). Peterson käyttää tähän kaikkia kolmea johdinta; meidän valintamme on niistä yleisin, <Hv>-iapos</Hv>.

</Fi>

<En>

The stem takes *iapos*. An *i* stem merges its *i* into the suffix (<Hv>sindigon</Hv> → <Hv>sindiapos</Hv>), and after *ñ* the suffix loses its *i* (<Hv>ōños</Hv> → <Hv>ōñapos</Hv>). Peterson uses all three diminutives for this; our choice is the most common of them, <Hv>-iapos</Hv>.

</En>

<Ex><Hv l="brōzi">brōzi</Hv><Seam s="brōz" e="iapos" /></Ex>

<Ex><Hv>gēlion</Hv><Seam s="gēl" e="iapos" /></Ex>

<Ex><Hv>ūly</Hv><Seam s="ūl" e="iapos" /></Ex>

<Alt row="small.new.noun" sense="-ītsos.small" fi="pienempi asia, joka liittyy X:ään" en="a smaller thing related to X">

<Ex><Hv>qēlos</Hv><Seam s="qēl" e="ītsos" /></Ex>

<Ex><Hv>ōños</Hv><Seam s="ōñ" e="ītsos" /></Ex>

<Ex><Hv>udlinon</Hv><Seam s="udlin" e="ītsos" /></Ex>

</Alt>

<Alt row="small.new.noun" sense="-anna.small" fi="erityinen osa tai kappale" en="a specialised part or piece">

<Ex><Hv l="bartos">bartos</Hv><Seam s="bart" e="anna" /></Ex>

<Ex><Hv>ziksos</Hv><Seam s="ziks" e="anna" /></Ex>

<Ex><Hv>mentys</Hv><Seam s="ment" e="anna" /></Ex>

</Alt>

</Use>

</EndingCard>

<EndingCard id="iso">

<Fi>

Kooltaan isompi tai muuten mahtavampi X. Tähän ei suomessa ole johdinta.

</Fi>

<En>

A physically bigger X, or a greater one. Finnish has no suffix for this.

</En>

<Use row="big.size.noun" fi="Kooltaan isompi X" en="A physically bigger X">

<Fi>

Vartaloon tulee *albar*. Johdin on lähtöisin sanasta <Hv>labar</Hv>.

</Fi>

<En>

The stem takes *albar*. The suffix comes from the word <Hv>labar</Hv>.

</En>

<Ex><Hv>dōron</Hv><Seam s="dōr" e="albar" /></Ex>

<Ex><Hv>nopon</Hv><Seam s="nop" e="albar" /></Ex>

<Ex><Hv>qīzy</Hv><Seam s="qīz" e="albar" /></Ex>

<Alt row="big.size.noun" sense="-āzma.big" fi="tavallista suurempi maiseman osa tai olento" en="an unusually great part of a landscape, or creature">

<Ex><Hv>embar</Hv><Seam s="emb" e="āzma" /></Ex>

<Ex><Hv>zokla</Hv><Seam s="zokl" e="āzma" /></Ex>

</Alt>

</Use>

<Use row="big.great.noun" fi="Mahtavampi, voimakkaampi tai arvokkaampi X" en="A greater, stronger or grander X">

<Fi>

Vartaloon tulee *āzma*. Jos sanalla on toinen vartalo, johdin tulee siihen (<Hv>jūlor</Hv> → <Hv>jūlrāzma</Hv>).

</Fi>

<En>

The stem takes *āzma*. A word with a second stem takes the suffix on that one (<Hv>jūlor</Hv> → <Hv>jūlrāzma</Hv>).

</En>

<Ex><Hv>jelmio</Hv><Seam s="jelm" e="āzma" /></Ex>

<Ex><Hv>vēdros</Hv><Seam s="vēdr" e="āzma" /></Ex>

<Ex><Hv>vīlībagon</Hv><Seam s="vīlīb" e="āzma" /></Ex>

<Alt row="big.great.noun" sense="-albar.big" fi="suuri tilaisuus tai täysikasvuinen olento" en="a big occasion, or a grown one">

<Ex><Hv>kisagon</Hv><Seam s="kis" e="albar" /></Ex>

<Ex><Hv>riña</Hv><Seam s="riñ" e="albar" /></Ex>

</Alt>

<Alt row="big.great.noun" sense="-io.augmentative" fi="X:n kaltainen isompi tai vahvempi olento tai esine" en="a related bigger or stronger creature or thing">

<Ex><Hv>kēli</Hv><Seam s="kēl" e="io" l="kēlio" /></Ex>

<Ex><Hv>nūmo</Hv><Seam s="nūm" e="io" /></Ex>

</Alt>

</Use>

</EndingCard>

<EndingCard id="suku">

<Fi>

Lapsenlapset ja sisarusten lapset saavat johtimen <Hv>-anna</Hv>, isovanhemmat johtimen <Hv>-āzma</Hv>.

</Fi>

<En>

Grandchildren and siblings’ children take <Hv>-anna</Hv>, grandparents <Hv>-āzma</Hv>.

</En>

<Use row="kin.down.noun" fi="Lapsenlapsi tai sisaruksen lapsi" en="A grandchild, or a sibling’s child">

<Fi>

Vartaloon tulee *anna*; *r*-loppuisissa johdin tulee toiseen vartaloon.

</Fi>

<En>

The stem takes *anna*; a word ending in *r* takes it on its second stem.

</En>

<Ex><Hv>trēsy</Hv><Seam s="trēs" e="anna" /></Ex>

<Ex><Hv>hāedar</Hv><Seam s="hāedr" e="anna" /></Ex>

</Use>

<Use row="kin.up.noun" fi="Isovanhempi" en="A grandparent">

<Fi>

Vartaloon tulee *āzma*.

</Fi>

<En>

The stem takes *āzma*.

</En>

<Ex><Hv>kepa</Hv><Seam s="kep" e="āzma" /></Ex>

<Ex><Hv>muña</Hv><Seam s="muñ" e="āzma" /></Ex>

</Use>

</EndingCard>

<EndingCard id="kaltainen" like="-mainen, -llinen, -inen">

<Fi>

Adjektiivi substantiivista: X:n kaltainen, X:ään liittyvä tai X:stä tehty.

</Fi>

<En>

An adjective from a noun: like X, having to do with X, or made of X.

</En>

<Use row="like.noun" fi="X:n kaltainen tai X:ään liittyvä" en="Like X, or having to do with X">

<Fi>

Vartaloon tulee *enka*. Jos sanalla on toinen vartalo, johdin tulee siihen.

</Fi>

<En>

The stem takes *enka*. A word with a second stem takes the suffix on that one.

</En>

<Ex><Hv>labar</Hv><Seam s="labr" e="enka" /></Ex>

<Ex><Hv>morghon</Hv><Seam s="morgh" e="enka" /></Ex>

<Ex><Hv l="glaeson">glaeson</Hv><Seam s="glaes" e="enka" /></Ex>

<Ex ours><Hv>vēdros</Hv><Seam s="vēdr" e="enka" /></Ex>

<Alt row="like.noun" sense="-ōñe.like" fi="johonkin piiriin kuuluva tai sen luonteinen" en="belonging to a domain, or having its character">

<Ex><Hv>dārys</Hv><Seam s="dār" e="ōñe" /></Ex>

<Ex><Hv>oktion</Hv><Seam s="okt" e="ōñe" /></Ex>

<Ex><Hv>kārilla</Hv><Seam s="kārill" e="ōñe" /></Ex>

</Alt>

</Use>

<Use row="made-of.noun" fi="X:stä tehty" en="Made of X">

<Fi>

Sama *enka*.

</Fi>

<En>

The same *enka*.

</En>

<Ex><Hv>gēlion</Hv><Seam s="gēl" e="enka" /></Ex>

<Ex><Hv>dōron</Hv><Seam s="dōr" e="enka" /></Ex>

</Use>

</EndingCard>

<EndingCard id="kotoisin" like="-lainen">

<Fi>

Adjektiivi paikasta, ilmansuunnasta tai sijainnista: sieltä kotoisin oleva tai sinne kuuluva.

</Fi>

<En>

An adjective from a place, a direction or a position: coming from it or belonging to it.

</En>

<Use row="from.noun" fi="Paikasta, ilmansuunnasta tai sijainnista" en="From a place, a direction or a position">

<Fi>

Vartaloon tulee *īha*.

</Fi>

<En>

The stem takes *īha*.

</En>

<Ex><Hv>Valyria</Hv><Seam s="valyr" e="īha" /></Ex>

<Ex><Hv>endia</Hv><Seam s="end" e="īha" l="endīha" /></Ex>

<Ex><Hv>vamio</Hv><Seam s="vam" e="īha" /></Ex>

<Alt row="from.noun" sense="-ōñe.origin" fi="joessa tai meressä elävä tai sieltä tuleva; myös pohjoinen ja eteläinen" en="living in or coming from a river or the sea; also northern and southern">

<Ex><Hv>jelmor</Hv><Seam s="jelm" e="ōñe" /></Ex>

<Ex><Hv>qelbar</Hv><Seam s="qelb" e="ōñe" /></Ex>

</Alt>

<Alt row="from.noun" sense="-īha.from-noun" fi="substantiivina: henkilö, joka on kotoisin X:stä" en="as a noun: a person from X">

<Ex><Hv>endia</Hv><Seam s="end" e="īha" l="endīha-2" /></Ex>

</Alt>

</Use>

</EndingCard>

<EndingCard id="ilman" like="-ton">

<Fi>

Adjektiivi substantiivista: jolla ei ole X:ää.

</Fi>

<En>

An adjective from a noun: without X.

</En>

<Use row="without.noun" fi="Substantiivista" en="From a noun">

<Fi>

Vartaloon tulee sidevokaali *o* ja sen perään *qitta*.

</Fi>

<En>

The stem takes a linking *o* and then *qitta*.

</En>

<Ex><Hv>laehurlion</Hv><Seam s="laehurl" e="oqitta" /></Ex>

<Ex><Hv>aeny</Hv><Seam s="aen" e="oqitta" /></Ex>

<Ex><Hv>sādo</Hv><Seam s="sād" e="oqitta" /></Ex>

</Use>

</EndingCard>

<EndingCard id="hyvin">

<Fi>

Adjektiivi, joka tarkoittaa ’hyvin X’. Uusi sana on <Term id="adjektiiviluokka">III-luokan</Term> adjektiivi, vaikka kantasana olisi <Term id="adjektiiviluokka">I-luokkaa</Term>.

</Fi>

<En>

An adjective meaning very X. The new word is a <Term id="adjektiiviluokka">class III</Term> adjective, even when its base is <Term id="adjektiiviluokka">class I</Term>.

</En>

<Use row="very.adjective" fi="Adjektiivista" en="From an adjective">

<Fi>

Vartaloon tulee *ēgrie*.

</Fi>

<En>

The stem takes *ēgrie*.

</En>

<Ex><Hv>brada</Hv><Seam s="brad" e="ēgrie" /></Ex>

<Ex><Hv>kempa</Hv><Seam s="kemp" e="ēgrie" /></Ex>

<Ex><Hv>onga</Hv><Seam s="ong" e="ēgrie" /></Ex>

</Use>

</EndingCard>

<EndingCard id="vastakohta" like="epä-">

<Fi>

X:n vastakohta tai teon kumoaminen, ei pelkkä kielto: heikko on vahvan vastakohta.

</Fi>

<En>

The opposite of X, or undoing it, not a plain negation: weak is the opposite of strong.

</En>

<Use row="opposite" fi="Adjektiivista, verbistä tai substantiivista" en="From an adjective, a verb or a noun">

<Fi>

Etuliite <Hv>nā-</Hv> tulee koko sanan eteen ja *rh* muuttuu *r*:ksi (<Hv>rhēdeta</Hv> → <Hv>nārēdeta</Hv>).

</Fi>

<En>

The prefix <Hv>nā-</Hv> goes before the whole word, and *rh* becomes *r* (<Hv>rhēdeta</Hv> → <Hv>nārēdeta</Hv>).

</En>

<Ex><Hv>kostōba</Hv><Seam s="nā" e="kostōba" pre /></Ex>

<Ex><Hv>bēmagon</Hv><Seam s="nā" e="bēmagon" pre /></Ex>

<Ex><Hv>geltigon</Hv><Seam s="nā" e="geltigon" pre /></Ex>

<Alt row="opposite" sense="dor-.negative" fi="’ei X’ adjektiivissa tai partisiipissa, ei koskaan verbissä" en="‘not X’ on an adjective or a participle, never on a verb">

<Ex><Hv>olvie</Hv><Seam s="dor" e="olvie" pre /></Ex>

<Ex><Hv fi="koulutettu, opetettu" en="trained, tutored">botēda</Hv><Seam s="do" e="botēda" pre /></Ex>

<Fi>

Verbi kielletään sanalla <Hv>daor</Hv>.

</Fi>

<En>

A verb is negated with <Hv>daor</Hv>.

</En>

</Alt>

</Use>

</EndingCard>

<EndingCard id="verbi-adjektiivista" like="-ntaa, -ntua, -ta">

<Fi>

Verbi adjektiivista: tehdä jostakin X tai tulla X:ksi.

</Fi>

<En>

A verb from an adjective: to make something X, or to become X.

</En>

<Use row="make.adjective" fi="Tehdä jostakin X" en="To make something X">

<Fi>

<Term id="adjektiiviluokka">I-luokan</Term> vartaloon tulee *emagon*. <Term id="adjektiiviluokka">II-luokassa</Term> *emagon* tulee juureen (<Hv>drēje</Hv> → <Hv>drējemagon</Hv>) tai juuren viimeinen konsonantti muuttuu *m*:ksi ja johdin on *magon* (<Hv>adere</Hv> → <Hv>ademmagon</Hv>); Peterson käyttää kumpaakin tapaa yhtä usein. Verbi taipuu kuten <Hv>emagon</Hv>, ja perfektissä on *-ēd-*.

</Fi>

<En>

A <Term id="adjektiiviluokka">class I</Term> stem takes *emagon*. In <Term id="adjektiiviluokka">class II</Term>, *emagon* goes on the root (<Hv>drēje</Hv> → <Hv>drējemagon</Hv>), or the root’s last consonant becomes *m* and the suffix is *magon* (<Hv>adere</Hv> → <Hv>ademmagon</Hv>); Peterson has as many of each. The verb conjugates like <Hv>emagon</Hv>, with the perfect *-ēd-*.

</En>

<Ex><Hv>dija</Hv><Seam s="dij" e="emagon" /></Ex>

<Ex><Hv>lyka</Hv><Seam s="lyk" e="emagon" /></Ex>

<Ex><Hv>dāez</Hv><Seam s="dāer" e="emagon" /></Ex>

</Use>

<Use row="become.adjective" fi="Tulla X:ksi" en="To become X">

<Fi>

Vartaloon tulee *ūljagon* ja perfektissä on *-ūlt-*. Vanha johdin <Hv>-iñagon</Hv> ei enää tee uusia verbejä (katso alta).

</Fi>

<En>

The stem takes *ūljagon*, with the perfect *-ūlt-*. The old <Hv>-iñagon</Hv> no longer makes new verbs (below).

</En>

<Ex><Hv>kasta</Hv><Seam s="kast" e="ūljagon" /></Ex>

<Ex><Hv>lōta</Hv><Seam s="lōt" e="ūljagon" /></Ex>

<Ex><Hv>iosre</Hv><Seam s="iosr" e="ūljagon" /></Ex>

</Use>

</EndingCard>

<EndingCard id="verbi-verbista" like="-ttaa">

<Fi>

Verbi verbistä: saada joku tekemään, tehdä uudelleen tai tehdä jotakin samantapaista. Muut verbien johtimet, kuten <Hv>-egon</Hv>: <TopicLink id="suffixes" />.

</Fi>

<En>

A verb from a verb: to make someone do it, to do it again, or to do something close to it. The other verb suffixes, such as <Hv>-egon</Hv>: <TopicLink id="suffixes" />.

</En>

<Use row="cause.verb" fi="Saada joku tai jokin tekemään" en="To make someone or something do it">

<Fi>

Konsonanttivartaloon tulee *ikagon*, vokaalivartaloon *kagon*. Tällaisen <Term id="kausatiivi">kausatiivin</Term> perfektissä on *-tt-*.

</Fi>

<En>

A consonant stem takes *ikagon*, a vowel stem *kagon*. Such a <Term id="kausatiivi">causative</Term> takes the perfect *-tt-*.

</En>

<Ex><Hv>aeragon</Hv><Seam s="aer" e="ikagon" /></Ex>

<Ex><Hv>kisagon</Hv><Seam s="kis" e="ikagon" /></Ex>

<Ex><Hv>ropagon</Hv><Seam s="ropa" e="kagon" /></Ex>

<Alt row="cause.verb" sense="-emagon.factitive" fi="panna joku tai jokin johonkin tilaan tai asentoon" en="to put someone or something into a state or a position">

<Ex><Hv>kiragon</Hv><Seam s="kir" e="emagon" /></Ex>

<Ex><Hv>vilagon</Hv><Seam s="vil" e="emagon" /></Ex>

<Ex><Hv>iōragon</Hv><Seam s="iōr" e="emagon" /></Ex>

</Alt>

</Use>

<Use row="again.verb" fi="Tehdä uudelleen" en="To do it again">

<Fi>

Etuliite <Hv>ā-</Hv> tulee verbin eteen, vokaalin edellä <Hv>ar-</Hv> (<Hv>otāpagon</Hv> → <Hv>arotāpagon</Hv>). Ostoksen palauttaminen tehdään kuitenkin vastakohdan etuliitteellä: <Hv>nāsindigon</Hv>.

</Fi>

<En>

The prefix <Hv>ā-</Hv> goes before the verb, <Hv>ar-</Hv> before a vowel (<Hv>otāpagon</Hv> → <Hv>arotāpagon</Hv>). Returning a purchase, though, takes the prefix of the opposite: <Hv>nāsindigon</Hv>.

</En>

<Ex><Hv>sikagon</Hv><Seam s="ā" e="sikagon" pre /></Ex>

<Ex><Hv>māzigon</Hv><Seam s="ā" e="māzigon" pre /></Ex>

<Alt row="again.verb" sense="-ligon.again" fi="tehdä kokonaan uudelleen, luoda alusta" en="to redo completely, to make anew">

<Ex><Hv>verdagon</Hv><Seam s="verd" e="ligon" /></Ex>

</Alt>

</Use>

<Use row="related.verb" fi="Merkitykseltään läheinen verbi" en="A verb of a related meaning">

<Fi>

Vartaloon lisätään sen viimeinen vokaali pitkänä ja sen perään *bagon*. Merkitys on aina sanakohtainen.

</Fi>

<En>

The stem takes a long copy of its last vowel and then *bagon*. The meaning is always the word’s own.

</En>

<Ex><Hv>kisagon</Hv><Seam s="kis" e="ībagon" /></Ex>

<Ex><Hv>jemagon</Hv><Seam s="jem" e="ēbagon" /></Ex>

<Ex><Hv>pāsagon</Hv><Seam s="pās" e="ābagon" /></Ex>

</Use>

</EndingCard>

<EndingCard id="verbi-substantiivista" like="-ta, -ita, -ttaa">

<Fi>

Verbi substantiivista: tehdä jotakin X:llä, saada X aikaan tai muuttua X:ksi.

</Fi>

<En>

A verb from a noun: to do something with X, to bring X about, or to turn into X.

</En>

<Use row="use.noun" fi="Tehdä jotakin X:llä tai panna X jonkin päälle" en="To do something with X, or to put X on something">

<Fi>

Vartaloon tulee *uragon*. Jos sanalla on toinen vartalo, johdin tulee siihen (<Hv>ñūvir</Hv> → <Hv>ñūvruragon</Hv>). Perfektissä on *-urt-*.

</Fi>

<En>

The stem takes *uragon*. A word with a second stem takes the suffix on that one (<Hv>ñūvir</Hv> → <Hv>ñūvruragon</Hv>). The perfect is *-urt-*.

</En>

<Ex><Hv>belmon</Hv><Seam s="belm" e="uragon" /></Ex>

<Ex><Hv>sādo</Hv><Seam s="sād" e="uragon" /></Ex>

<Ex ours><Hv>ȳdravos</Hv><Seam s="ȳdrav" e="uragon" fi="soittaa (puhelimella)" en="to call (on the phone)" /></Ex>

</Use>

<Use row="cause.noun" fi="Saada X aikaan" en="To bring X about">

<Fi>

Vartaloon tulee *ikagon*. Petersonin kolmesta sanasta kaksi on idiomaattisia, ja kolmas, <Hv>zonkagon</Hv>, on vanha kausatiivi (katso alta).

</Fi>

<En>

The stem takes *ikagon*. Two of Peterson’s three words are idiomatic, and the third, <Hv>zonkagon</Hv>, is an old causative (see below).

</En>

<Ex><Hv>qogron</Hv><Seam s="qogr" e="ikagon" /></Ex>

<Ex><Hv>volpe</Hv><Seam s="volp" e="ikagon" /></Ex>

</Use>

<Use row="become.noun" fi="Muuttua X:ksi tai toimia kuin X" en="To turn into X, or to act like X">

<Fi>

Vartaloon tulee *ākogon*. Petersonin sanojen merkitystä ei voi päätellä osista: ’susi’ → ’houkutella’, ’rohkea ihminen’ → ’rohkaista’.

</Fi>

<En>

The stem takes *ākogon*. The meaning of Peterson’s words cannot be read from their parts: ‘wolf’ → ‘entice’, ‘brave person’ → ‘encourage’.

</En>

<Ex><Hv>zokla</Hv><Seam s="zokl" e="ākogon" /></Ex>

<Ex><Hv>nēdys</Hv><Seam s="nēd" e="ākogon" /></Ex>

<Ex><Hv>naena</Hv><Seam s="naen" e="ākogon" /></Ex>

</Use>

</EndingCard>

<EndingCard id="etuliitteet">

<Fi>

Nämä <Term id="etuliite">etuliitteet</Term> tekevät verbistä uuden verbin, jonka merkitystä ei aina voi arvata. Etuliitteiden järjestys ja muut etuliitteet: <TopicLink id="prefixes" />.

</Fi>

<En>

These <Term id="etuliite">prefixes</Term> make a new verb from a verb, often with an unpredictable meaning. The order of prefixes and the other prefixes: <TopicLink id="prefixes" />.

</En>

<Use row="begin.verb" fi="Ryhtyä tekemään" en="To come to do it">

<Fi>

Etuliitteen *z* muuttuu *s*:ksi soinnittoman äänteen edellä, *z* ja verbin alku-*r* sulautuvat *j*:ksi (<Hv>raqagon</Hv> → <Hv>majaqagon</Hv>) ja verbin alku-*h* katoaa.

</Fi>

<En>

The prefix’s *z* becomes *s* before a voiceless sound, *z* and the verb’s first *r* merge into *j* (<Hv>raqagon</Hv> → <Hv>majaqagon</Hv>), and the verb’s first *h* drops.

</En>

<Ex><Hv>iōragon</Hv><Seam s="maz" e="iōragon" pre /></Ex>

<Ex><Hv>ilībagon</Hv><Seam s="maz" e="ilībagon" pre /></Ex>

<Ex><Hv>kiragon</Hv><Seam s="mas" e="kiragon" pre /></Ex>

</Use>

<Use row="temporary.verb" fi="Tehdä hetken" en="To do it for a while">

<Fi>

Konsonantin edellä <Hv>mī-</Hv>, vokaalin edellä <Hv>mīv-</Hv>.

</Fi>

<En>

Before a consonant <Hv>mī-</Hv>, before a vowel <Hv>mīv-</Hv>.

</En>

<Ex><Hv>keligon</Hv><Seam s="mī" e="keligon" pre /></Ex>

<Ex><Hv>dēmagon</Hv><Seam s="mī" e="dēmagon" pre /></Ex>

<Ex><Hv>tepagon</Hv><Seam s="mī" e="tepagon" pre /></Ex>

</Use>

<Use row="continue.verb" fi="Jatkaa, tehdä yhä" en="To keep doing it">

<Fi>

Konsonantin edellä <Hv>jo-</Hv>, vokaalin edellä <Hv>jor-</Hv>.

</Fi>

<En>

Before a consonant <Hv>jo-</Hv>, before a vowel <Hv>jor-</Hv>.

</En>

<Ex><Hv>epagon</Hv><Seam s="jor" e="epagon" pre /></Ex>

<Ex><Hv>nevegon</Hv><Seam s="jo" e="nevegon" pre /></Ex>

</Use>

<Use row="intensive.verb" fi="Tehdä voimalla tai perusteellisesti" en="To do it hard or thoroughly">

<Fi>

Etuliitteen *z* muuttuu *s*:ksi soinnittoman äänteen edellä, ja *z* ja verbin alku-*r* sulautuvat *j*:ksi.

</Fi>

<En>

The prefix’s *z* becomes *s* before a voiceless sound, and *z* and the verb’s first *r* merge into *j*.

</En>

<Ex><Hv>sēnagon</Hv><Seam s="os" e="sēnagon" pre /></Ex>

<Ex><Hv>dakogon</Hv><Seam s="oz" e="dakogon" pre /></Ex>

<Ex><Hv>pikagon</Hv><Seam s="os" e="pikagon" pre /></Ex>

</Use>

<Use row="pejorative.verb" fi="Tehdä huonosti tai väärin" en="To do it badly or wrongly">

<Fi>

Etuliitteen *n* muuttuu *m*:ksi huuliäänteen (kuten *p* tai *b*) edellä, ja *n* ja verbin alku-*l* sulautuvat *ll*:ksi.

</Fi>

<En>

The prefix’s *n* becomes *m* before a lip sound such as *p* or *b*, and *n* and the verb’s first *l* merge into *ll*.

</En>

<Ex><Hv>pālegon</Hv><Seam s="qrim" e="pālegon" pre /></Ex>

<Ex><Hv>brōzagon</Hv><Seam s="qrim" e="brōzagon" pre /></Ex>

</Use>

</EndingCard>

<H fi="Näitä ei käytetä uusiin sanoihin" en="Not used for new words" />

<Fi>

Nämä johtimet näkyvät Petersonin sanoissa, mutta uusia sanoja niillä ei tehdä:

- <Hv>-iñagon</Hv> on vanha ’tulla joksikin’ -johdin (<Hv>ēdrugon</Hv> → <Hv>ēdrȳñagon</Hv>). Petersonin mukaan se on kuollut; nykyään käytetään johdinta <Hv>-ūljagon</Hv>.
- <Hv>-āpsa</Hv> ja adjektiivin <Hv>-lie</Hv> ovat wikin mukaan harvinaisia eikä niillä luultavasti enää tehdä uusia sanoja.
- <Hv>-ria</Hv> on vain kolmessa paikkaa tarkoittavassa sanassa.
- <Hv>-kydoso</Hv> tekee tapaa ilmaisevia adverbeja vain pronomineista johdetuista adjektiiveista.
- Lukusanojen <Hv>-ēpsa</Hv> ja järjestyslukujen <Hv>-lie</Hv> esitellään lukusanojen yhteydessä (<TopicLink id="numerals" />).
- Vanha kausatiivi, kuten <Hv>zonkagon</Hv> sanasta <Hv>zomir</Hv>, ei ole malli uusille verbeille.

</Fi>

<En>

These suffixes appear in Peterson’s words, but new words are not made with them:

- <Hv>-iñagon</Hv> is the old ‘become’ suffix (<Hv>ēdrugon</Hv> → <Hv>ēdrȳñagon</Hv>). Peterson calls it dead; <Hv>-ūljagon</Hv> is used today.
- <Hv>-āpsa</Hv> and the adjectival <Hv>-lie</Hv> are rare according to the wiki and most likely make no new words any more.
- <Hv>-ria</Hv> is found in only three words, all of them places.
- <Hv>-kydoso</Hv> makes manner adverbs only from pronominal adjectives.
- The <Hv>-ēpsa</Hv> of the tens and the <Hv>-lie</Hv> of the ordinals belong to the numeral rules (<TopicLink id="numerals" />).
- The old causative, as in <Hv>zonkagon</Hv> from <Hv>zomir</Hv>, is not a model for new verbs.

</En>

</Topic>
